import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { PlatformService } from './platform.service';
import { AuthService } from './auth.service';

/**
 * DeepLinkService
 *
 * Handles deep link callbacks from mobile OAuth authentication.
 *
 * On mobile platforms (iOS/Android), after the user authenticates with Keycloak
 * in the system browser, they are redirected back to the app using a custom
 * URL scheme (myabvv://oauth-callback). This service listens for these deep
 * links and routes them to the AuthService for token processing.
 *
 * Deep Link Flow:
 * 1. User clicks login in app
 * 2. App opens system browser with Keycloak URL
 * 3. User authenticates in browser
 * 4. Keycloak redirects to myabvv://oauth-callback?code=xxx&state=yyy
 * 5. iOS/Android opens the app with this URL
 * 6. DeepLinkService intercepts the URL
 * 7. Navigates to /auth/callback route
 * 8. AuthService.handleCallback() processes the code
 *
 * This service is ONLY active on mobile platforms.
 */
@Injectable({
  providedIn: 'root'
})
export class DeepLinkService {
  private platformService = inject(PlatformService);
  private router = inject(Router);
  private authService = inject(AuthService);

  private listenerActive = false;

  /**
   * Initialize deep link listener
   *
   * Sets up the Capacitor App plugin to listen for appUrlOpen events.
   * Only activates on mobile platforms.
   *
   * Call this in APP_INITIALIZER or app component ngOnInit.
   */
  init(): void {
    // Only activate on mobile platforms
    if (!this.platformService.isMobile()) {
      return;
    }

    if (this.listenerActive) {
      console.warn('[DeepLinkService.init] Listener already active, skipping re-initialization');
      return;
    }

    // Listen for app URL open events
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.handleDeepLink(event.url);
    });

    this.listenerActive = true;
  }

  /**
   * Handle incoming deep link
   *
   * @param url The deep link URL (e.g., myabvv://oauth-callback?code=xxx)
   * @private
   */
  private handleDeepLink(url: string): void {
    try {
      // Parse the URL
      const urlObj = new URL(url);

      if (this.isOAuthCallback(urlObj)) {
        // Navigate to the callback route with query parameters preserved
        // The AuthService will handle the OAuth callback processing
        // Build the callback URL
        const callbackUrl = `/auth/callback${urlObj.search}`;

        // Close the in-app browser overlay
        Browser.close().catch(() => {});

        this.router.navigateByUrl(callbackUrl).then(
          () => {},
          (navError) => {
            console.error('[DeepLinkService.handleDeepLink] ✗ Navigation failed:', navError);
            console.error('[DeepLinkService.handleDeepLink] Error details:', JSON.stringify(navError, null, 2));
          }
        );
      } else if (
        urlObj.hostname === 'signature-result' || urlObj.host === 'signature-result' ||
        urlObj.pathname === '/signature-result'
      ) {
        // Handle signature result — matches both:
        //   - custom scheme: myabvv://signature-result?status=success  (Android)
        //   - Universal Link: https://myabvv-pre.abvv.be/signature-result?status=success  (iOS)
        const status = urlObj.searchParams.get('status') || 'unknown';

        // Close the in-app browser overlay (SFSafariViewController on iOS, Chrome Custom Tab on Android)
        Browser.close().catch(() => {});

        // Navigate to inbox with signature status
        this.router.navigateByUrl(`/unemployment/inbox?status=${status}`).then(
          () => {},
          (err) => console.error('[DeepLinkService.handleDeepLink] ✗ Navigation failed:', err)
        );
      }
      // Other deep links are ignored
    } catch (error) {
      console.error('[DeepLinkService.handleDeepLink] ========== ERROR ==========');
      console.error('[DeepLinkService.handleDeepLink] Failed to parse deep link URL');
      console.error('[DeepLinkService.handleDeepLink] Error type:', error?.constructor?.name);
      console.error('[DeepLinkService.handleDeepLink] Error message:', error?.message);
      console.error('[DeepLinkService.handleDeepLink] Error stack:', error?.stack);
      console.error('[DeepLinkService.handleDeepLink] ========== FAILURE ==========');
    }
  }

  /**
   * Check if URL is an OAuth callback
   *
   * OAuth callbacks typically have 'code' and 'state' query parameters
   *
   * @param url The parsed URL object
   * @private
   */
  private isOAuthCallback(url: URL): boolean {
    const hasCode = url.searchParams.has('code');
    const hasState = url.searchParams.has('state');
    const isCallbackPath = url.pathname === '/oauth-callback' ||
                          url.pathname === '/auth/callback' ||
                          url.host === 'oauth-callback';

    return hasCode && hasState && (isCallbackPath || url.host === 'oauth-callback');
  }

  /**
   * Remove deep link listener
   *
   * Call this when the service is destroyed (e.g., in ngOnDestroy)
   */
  async destroy(): Promise<void> {
    if (this.listenerActive && this.platformService.isMobile()) {
      await App.removeAllListeners();
      this.listenerActive = false;
    }
  }
}
